import { useContext, useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { AuthContext } from "../../../contexts";
import CustomTag from "../../../ui/CustomTag";
import CustomSelect from "../../../ui/CustomSelect";
import CustomList from "../../../ui/CustomList";
import { CircularProgress } from "@mui/material";
import { toast } from "react-toastify";
import { toastOptions } from "../../../assets/constants";
import CustomPageHeader from "../../../ui/CustomPageHeader";
import { NetworkIcon } from "@phosphor-icons/react";

const DomainsList = () => {
    const { token } = useContext(AuthContext);
    const [searchParams, setSearchParams] = useSearchParams();
    const [domains, setDomains] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [isLoaded, setIsLoaded] = useState(false);
    const [mappingLoading, setMappingLoading] = useState(false);

    const currentPage = parseInt(searchParams.get("page")) || 0;
    const pageSize = parseInt(searchParams.get("pageSize")) || 50;

    const handlePaginationChange = useCallback(
        ({ pageIndex, pageSize: newPageSize }) => {
            setSearchParams((params) => {
                params.set("page", pageIndex.toString());
                params.set("pageSize", newPageSize.toString());
                return params;
            });
        },
        [setSearchParams]
    );

    const loadData = useCallback(async () => {
        try {
            const [domainsResponse, customersResponse] = await Promise.all([
                axios.get(`${process.env.REACT_APP_UPA_API_HOST}ovh/all-domains`, { headers: { Authorization: `Bearer ${token}` } }),
                axios.get(`${process.env.REACT_APP_UPA_API_HOST}pennylane/customers`, { headers: { Authorization: `Bearer ${token}` } }),
            ]);

            setDomains(domainsResponse.data.domains || []);
            setCustomers(customersResponse.data || []);
        } catch (error) {
            console.error("Erreur chargement données:", error);
            toast.error("Erreur lors du chargement", toastOptions);
        } finally {
            setIsLoaded(true);
        }
    }, [token]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const performMapping = useCallback(
        async (endpoint, method, domainName, clientId = null) => {
            try {
                setMappingLoading(true);
                const config = { headers: { Authorization: `Bearer ${token}` } };

                if (method === "POST") {
                    await axios.post(endpoint, { domainName, clientId }, config);
                } else {
                    await axios.delete(endpoint, config);
                }

                setDomains((prevDomains) =>
                    prevDomains.map((domain) =>
                        domain.domainName === domainName ? { ...domain, altelisId: method === "POST" ? customers.find((c) => c.id === clientId)?.reference || null : null } : domain
                    )
                );

                toast.success(`Domaine ${domainName} ${method === "POST" ? "mappé" : "dé-mappé"} avec succès`, toastOptions);
            } catch (error) {
                console.error(`Erreur ${method === "POST" ? "mapping" : "unmapping"}:`, error);
                toast.error(`Erreur lors du ${method === "POST" ? "mapping" : "dé-mapping"}`, toastOptions);
            } finally {
                setMappingLoading(false);
            }
        },
        [token, customers]
    );

    const mapDomain = useCallback(
        (domainName, clientId) => {
            performMapping(`${process.env.REACT_APP_UPA_API_HOST}ovh/map-domain`, "POST", domainName, clientId);
        },
        [performMapping]
    );

    const unmapDomain = useCallback(
        (domainName) => {
            performMapping(`${process.env.REACT_APP_UPA_API_HOST}ovh/unmap/${domainName}`, "DELETE", domainName);
        },
        [performMapping]
    );

    const clientOptions = useMemo(() => customers.map(({ id, name }) => ({ value: id, label: name })), [customers]);

    const getMappedClient = useCallback((domain) => (domain.altelisId ? customers.find((customer) => customer.reference === domain.altelisId) : null), [customers]);

    const handleClientChange = useCallback(
        (domain, selectedOption) => {
            selectedOption && mapDomain(domain.domainName, selectedOption.value);
        },
        [mapDomain]
    );

    const ActionCell = useCallback(
        ({ domain }) => {
            const mappedClient = getMappedClient(domain);

            return mappedClient ? (
                <button onClick={() => unmapDomain(domain.domainName)} disabled={mappingLoading} className="button-large cancel-button">
                    Dé-mapper
                </button>
            ) : (
                <CustomSelect
                    options={clientOptions}
                    placeholder="Sélectionner..."
                    onChange={(selected) => handleClientChange(domain, selected)}
                    isSearchable
                    isClearable
                    isDisabled={mappingLoading}
                    width={200}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                />
            );
        },
        [clientOptions, getMappedClient, handleClientChange, unmapDomain, mappingLoading]
    );

    const domainCols = useMemo(
        () => [
            {
                Header: "Domaine",
                accessor: "domainName",
                icon: "website",
                Cell: ({ cell: { value } }) => <CustomTag value={value} />,
            },
            {
                Header: "Client",
                accessor: "mappedClient",
                icon: "user",
                Cell: ({ row }) => {
                    const mappedClient = getMappedClient(row.original);
                    return mappedClient ? <CustomTag value={mappedClient.name} className="success" /> : <CustomTag value="Non mappé" className="deactivated" />;
                },
            },
            {
                Header: "Actions",
                accessor: "actions",
                icon: "action",
                Cell: ({ row }) => <ActionCell domain={row.original} />,
            },
        ],
        [getMappedClient]
    );

    if (!isLoaded) {
        return (
            <div className="loader-container absolute">
                <CircularProgress size={30} />
            </div>
        );
    }

    return (
        <div className="c-app__view">
            <CustomPageHeader title="Gestion des NDD" titleIcon={{ icon: NetworkIcon }} />

            {domains.length === 0 ? (
                <CustomTag value="Aucun domaine OVH disponible" className="deactivated" />
            ) : (
                <div className="tabs-container">
                    <CustomList
                        entity={domains}
                        columns={domainCols}
                        isSelectable={false}
                        isClickable={false}
                        isScrollable={true}
                        entityType="domains"
                        initialPageIndex={currentPage}
                        initialPageSize={pageSize}
                        onPaginationChange={handlePaginationChange}
                    />
                </div>
            )}
        </div>
    );
};

export default DomainsList;
